import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "../styles/video.css";
import play from "../assets/icons/play-video.svg";
import waterMark from "../assets/icons/water-mark-gist.png";
import styled from "styled-components";
import PropTypes from "prop-types";
import VideoLoader from "./VideoLoader";

const backgroundMusicMapper = {
  nuetral:
    "https://firebasestorage.googleapis.com/v0/b/colonoscopy-dev.appspot.com/o/GistMD%20Nuetral%20Soundtrack%20-%20Complete%20Master%20for%20Dialogue.mp3?alt=media&token=1fcfc68b-0166-435f-87cc-559a1b40b53b",
  optimistic:
    "https://firebasestorage.googleapis.com/v0/b/colonoscopy-dev.appspot.com/o/GistMD%20Optimistic%20Soundtrack.mp3?alt=media&token=861b876c-7c3a-4af8-969f-472abdf88a50",
  serious:
    "https://firebasestorage.googleapis.com/v0/b/colonoscopy-dev.appspot.com/o/GistMD%20Serious%20Soundtrack%20-%20Complete%20Master%20for%20Dialogue.mp3?alt=media&token=b8d1d53d-8ec2-49b3-9730-2507958bf854",
};

const defaultBackgroundMusic = "nuetral";

export const Player = ({
  src,
  autoFullScreen,
  passedRef,
  thumbnail,
  audioStartDelay,
  onLocationUpdate,
  backgroundMusic,
  onLoadMetadata,
  onPlayerPlaying,
  onTimeUpdate,
  backgroundMusicVolume,
  showGistmdWaterMark = true,
  ...props
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const audioRef = useRef(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const isVideoLoadingRef = useRef(true);
  
  // Keep ref in sync with state
  useEffect(() => {
    isVideoLoadingRef.current = isVideoLoading;
  }, [isVideoLoading]);

  const options = useMemo(
    () => ({
      controls: true,
      responsive: true,
      fluid: true,
      loop: false,
      preload: 'auto',
      poster: thumbnail,
      sources: [
      {
          src,
          type: "application/x-mpegURL",
        },
      ],
    }),
    [src, thumbnail]
  );

  const onPlayerPlay = useCallback(() => {
    onPlayerPlaying && onPlayerPlaying();
  }, [onPlayerPlaying]);

  const onPlayerReady = useCallback(() => {
    // Keep loader visible until canplay for thumbnail display
  }, []);

  const onPlayerCanPlay = useCallback(() => {
    // Hide loader when video can play - thumbnail was showing
    console.log('[Player] canplay event fired');
    setIsVideoLoading(false);
  }, []);

  const onPlayerLoadedData = useCallback(() => {
    // Fallback for mobile Safari which may not fire canplay
    console.log('[Player] loadeddata event fired');
    setIsVideoLoading(false);
  }, []);

  const onPlayerCanPlayThrough = useCallback(() => {
    // Another fallback for mobile
    console.log('[Player] canplaythrough event fired');
    setIsVideoLoading(false);
  }, []);

  const onPlayerPause = useCallback(() => {
    // When pausing the video pause the background music.
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, []);

  // Initallizing timer to edit the amount of time for the function to be trigger using the videoJs hook.
  const timer = useRef(0);
  const secondsDelta = 3;
  const audioPlayPendingRef = useRef(false);
  const audioUnsupportedRef = useRef(false);
  const onPlayerUpdateTime = useCallback(() => {
    const percentage =
      (playerRef.current.currentTime() / playerRef.current.duration()) * 100;
    const location = playerRef.current.currentTime();
    console.log('[Player] timeupdate:', { location, onTimeUpdate: !!onTimeUpdate });
    const audio = audioRef.current;
    if (
      audio &&
      audio.paused &&
      !audioPlayPendingRef.current &&
      !audioUnsupportedRef.current &&
      location >= audioStartDelay &&
      !playerRef.current.paused()
    ) {
      // Background music is optional; iOS can reject a second media element with NotSupportedError.
      audioPlayPendingRef.current = true;
      Promise.resolve(audio.play())
        .catch((error) => {
          if (error?.name === 'NotSupportedError') audioUnsupportedRef.current = true;
          console.log('[Player] Background music play failed (non-critical):', error?.name);
        })
        .finally(() => {
          audioPlayPendingRef.current = false;
        });
    }
    if (onTimeUpdate) {
      console.log('[Player] calling onTimeUpdate with location:', location);
      onTimeUpdate(location);
    }
    if (location > timer.current) {
      timer.current += secondsDelta;
      if (onLocationUpdate) onLocationUpdate(percentage, location);
    }
  }, [audioStartDelay, onLocationUpdate, onTimeUpdate]);

  const onPlayerSeeking = useCallback(() => {
    // On seeking we want to update the timer of the updateTime trigger to keep triggering after every 3 seconds and not less.
    const location = playerRef.current.currentTime();
    // On scroll to another location in the video change the music location also.
    if (audioRef.current) audioRef.current.currentTime = location;
    // Taking the maximum to handle backwards seeking.
    timer.current = Math.max(timer.current, location);
  }, []);

  const onPlayerEnded = useCallback(() => {
    // Prevent video from looping - ensure player stops at end
    if (playerRef.current) {
      playerRef.current.pause();
    }
  }, []);

  const setPlayerInitialSettings = useCallback(
    (element) => {
      // audio.current.volume = props.audioVolume;
      // set the props of the player:
      element.on("timeupdate", onPlayerUpdateTime);
      element.on("ready", onPlayerReady);
      element.on("canplay", onPlayerCanPlay);
      element.on("loadeddata", onPlayerLoadedData);
      element.on("canplaythrough", onPlayerCanPlayThrough);
      element.on("pause", onPlayerPause);
      element.on("seeking", onPlayerSeeking);
      element.on("play", onPlayerPlay);
      element.on("ended", onPlayerEnded);
      onLoadMetadata && element.on("loadedmetadata", onLoadMetadata);
    },
    [onLoadMetadata, onPlayerCanPlay, onPlayerCanPlayThrough, onPlayerLoadedData, onPlayerSeeking, onPlayerUpdateTime, onPlayerEnded, onPlayerPlay, onPlayerReady, onPlayerPause]
  );

  useEffect(() => {
    // Fallback: hide loader after 3 seconds if no events fire (mobile Safari)
    const loaderTimeout = setTimeout(() => {
      if (isVideoLoadingRef.current) {
        console.log('[Player] Timeout fallback: hiding loader');
        setIsVideoLoading(false);
      }
    }, 3000);

    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement("video-js");
      videoRef.current.appendChild(videoElement);
      if (!autoFullScreen) videoElement.setAttribute("playsInline", true);
      playerRef.current = videojs(videoElement, options, () =>
        setPlayerInitialSettings(playerRef.current)
      );

      const { bigPlayButton, textTrackDisplay } = playerRef.current;
      const image = document.createElement("img");
      image.style.width = "40%";
      image.setAttribute("src", play);
      // Sets the background music volume.
      audioRef.current.volume = backgroundMusicVolume;
      bigPlayButton.el_.appendChild(image);
      textTrackDisplay.on("click", () => {
        if (playerRef.current.paused()) playerRef.current.play();
        else playerRef.current.pause();
      });
      if (passedRef) passedRef.current = playerRef.current;
    } else {
      const player = playerRef.current;
      player.autoplay(options.autoplay);
      player.src(options.sources);
    }

    // Dispose the Video.js player when the functional component unmounts.
    return () => {
      clearTimeout(loaderTimeout);
      setIsVideoLoading(true);
      timer.current = 0;
      audioPlayPendingRef.current = false;
      audioUnsupportedRef.current = false;
      const player = playerRef.current;
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
        audioRef.current?.pause();
      }
    };
  }, [autoFullScreen, options, passedRef, setPlayerInitialSettings, videoRef]);

  return (
    <VideoContainer ref={videoRef} {...props}>
      {isVideoLoading && <VideoLoader thumbnail={thumbnail} />}
      <audio 
        src={backgroundMusicMapper[backgroundMusic]} 
        ref={audioRef}
        onError={(e) => {
          // Prevent audio errors from bubbling up to video error handler
          e.stopPropagation();
          console.log('Background music load error (non-critical):', e);
        }}
      />
      {showGistmdWaterMark &&
        <WaterMark src={waterMark} className="watermark" alt="waterMark" />
      }
    </VideoContainer>
  );
};

const VideoContainer = styled.div`
  position: relative;
`;

const WaterMark = styled.img`
  position: absolute;
  background-color: transparent;
  width: 100%;
  height: 100%;
  z-index: 2;
  inset: 0;
  pointer-events: none;
  border-radius: 15px;
`;

Player.propTypes = {
  src: PropTypes.string.isRequired,
  autoFullScreen: PropTypes.bool,
  passedRef: PropTypes.elementType,
  thumbnail: PropTypes.string,
  audioStartDelay: PropTypes.number,
  onLocationUpdate: PropTypes.func,
  backgroundMusic: PropTypes.string,
  onLoadMetadata: PropTypes.func,
  onPlayerPlaying: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  backgroundMusicVolume: PropTypes.number,
};

Player.defaultProps = {
  autoFullScreen: false,
  backgroundMusic: defaultBackgroundMusic,
  backgroundMusicVolume: 0.5,
};
